import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { bulkUploadAPI, paymentsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import {
  ArrowLeft,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  CreditCard,
} from 'lucide-react';

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await bulkUploadAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_anuncios.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.endsWith('.csv')) {
      toast.error('Selecciona un archivo CSV');
      return;
    }
    setFile(selected);
    setResult(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    if (!dropped.name.endsWith('.csv')) {
      toast.error('Suelta un archivo CSV');
      return;
    }
    setFile(dropped);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const response = await bulkUploadAPI.upload(file);
      setResult(response.data);
      if (response.data.created > 0) {
        toast.success(`${response.data.created} anuncio(s) creado(s) como borrador`);
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} fila(s) con errores`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al subir el archivo'));
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePayBulk = async () => {
    if (!result?.listing_ids?.length) return;
    // The backend computes which IDs are unpaid; we send all created IDs and it filters.
    try {
      toast.info('Redirigiéndote al pago...');
      const res = await paymentsAPI.createBulkCheckout(result.listing_ids, window.location.origin);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo iniciar el pago'));
    }
  };

  const formatCOP = (n) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

  return (
    <div className="min-h-screen bg-secondary" data-testid="bulk-upload-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-muted-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al panel
            </Button>
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            Carga masiva CSV
          </h1>
          <p className="text-muted-foreground mt-1">
            Sube varios anuncios de vehículos a la vez usando un archivo CSV
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Step 1: Download Template */}
        <div className="bg-white border border-border rounded-md p-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 p-3 rounded-sm shrink-0">
              <Download className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-1">
                Paso 1: Descarga la plantilla
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Descarga la plantilla CSV, completa los datos de tus vehículos y súbela aquí abajo.
              </p>
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                data-testid="download-template-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Descargar plantilla CSV
              </Button>
            </div>
          </div>
        </div>

        {/* CSV Column Guide */}
        <div className="bg-white border border-border rounded-md p-6">
          <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-3">
            Referencia de columnas CSV
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="csv-reference-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-semibold">Columna</th>
                  <th className="text-left py-2 px-3 font-semibold">Obligatorio</th>
                  <th className="text-left py-2 px-3 font-semibold">Descripción</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {[
                  ['category', 'Sí', 'trucks, trailers, camionetas, cars, motorcycles, boats, parts, engines, equipment'],
                  ['make', 'Sí', 'Fabricante del vehículo (p. ej., Freightliner, Peterbilt)'],
                  ['model', 'Sí', 'Modelo del vehículo (p. ej., Cascadia, 579)'],
                  ['year', 'Sí', 'Año (1900-2030)'],
                  ['price', 'Sí', 'Precio en COP (p. ej., 250000000)'],
                  ['condition', 'No', 'new, used (por defecto), certified, salvage'],
                  ['mileage', 'No', 'Kilometraje en km'],
                  ['engine_hours', 'No', 'Horas de motor'],
                  ['vin', 'No', 'Número de identificación del vehículo (VIN)'],
                  ['location_city', 'Sí', 'Ciudad'],
                  ['location_state', 'Sí', 'Departamento (p. ej., Antioquia)'],
                  ['description', 'No', 'Texto descriptivo del vehículo'],
                ].map(([col, req, desc]) => (
                  <tr key={col} className="border-b border-border/50">
                    <td className="py-2 px-3 font-mono text-xs text-foreground">{col}</td>
                    <td className="py-2 px-3">
                      {req === 'Sí' ? (
                        <Badge className="bg-red-100 text-red-700 text-xs">Obligatorio</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Opcional</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Step 2: Upload CSV */}
        <div className="bg-white border border-border rounded-md p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-accent/10 p-3 rounded-sm shrink-0">
              <Upload className="h-6 w-6 text-accent" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-1">
                Paso 2: Sube tu CSV
              </h2>
              <p className="text-sm text-muted-foreground">
                Todos los anuncios se crearán como borradores. Podrás publicarlos desde tu panel.
              </p>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".csv"
            className="hidden"
            data-testid="csv-file-input"
          />

          {/* Drop zone */}
          {!file ? (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-muted hover:border-accent hover:bg-accent/5 rounded-md p-10 text-center cursor-pointer transition-colors"
              data-testid="csv-dropzone"
            >
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-accent" />
              <p className="font-semibold mb-1">Haz clic para seleccionar o arrastra y suelta</p>
              <p className="text-sm text-muted-foreground">Solo archivos CSV</p>
            </div>
          ) : (
            <div className="border border-border rounded-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-accent" />
                  <div>
                    <p className="font-semibold" data-testid="selected-file-name">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClear}
                  disabled={uploading}
                  data-testid="clear-file-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {!result && (
                <Button
                  className="btn-accent w-full mt-4 h-12"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="upload-csv-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Subir y crear anuncios
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Upload Result */}
        {result && (
          <div className="bg-white border border-border rounded-md p-6 space-y-4" data-testid="upload-result">
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight">
              Resultados de la carga
            </h2>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-secondary rounded-sm text-center">
                <p className="text-2xl font-bold font-heading">{result.total_rows}</p>
                <p className="text-sm text-muted-foreground">Filas totales</p>
              </div>
              <div className="p-4 bg-green-50 rounded-sm text-center">
                <p className="text-2xl font-bold font-heading text-green-700">{result.created}</p>
                <p className="text-sm text-green-600">Creados</p>
              </div>
              <div className="p-4 bg-red-50 rounded-sm text-center">
                <p className="text-2xl font-bold font-heading text-red-700">{result.errors?.length || 0}</p>
                <p className="text-sm text-red-600">Errores</p>
              </div>
            </div>

            {result.created > 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <p className="text-sm text-green-700">
                  {result.created} anuncio(s) creado(s) como <strong>borrador</strong>. Ve a tu panel para revisarlos y publicarlos.
                </p>
              </div>
            )}

            {result.paid_required > 0 && (
              <div className="p-4 bg-accent/5 border border-accent/30 rounded-sm space-y-3" data-testid="bulk-payment-prompt">
                <div className="flex items-start gap-2">
                  <CreditCard className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold mb-1">
                      Pago requerido para publicar {result.paid_required} anuncio{result.paid_required > 1 ? 's' : ''}
                    </p>
                    <p className="text-muted-foreground">
                      {result.free_used > 0 && `${result.free_used} usaron tu cuota gratuita. `}
                      Los {result.paid_required} restantes cuestan {formatCOP(result.fee_per_listing)} c/u —
                      total <strong>{formatCOP(result.fee_per_listing * result.paid_required)}</strong>.
                    </p>
                  </div>
                </div>
                <Button className="btn-accent w-full" onClick={handlePayBulk} data-testid="pay-bulk-btn">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pagar y publicar todos
                </Button>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="font-semibold text-sm">Errores ({result.errors.length})</span>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {result.errors.map((err, idx) => (
                    <div key={idx} className="text-sm p-2 bg-red-50 rounded-sm" data-testid={`error-row-${err.row}`}>
                      <span className="font-mono font-semibold">Fila {err.row}:</span>{' '}
                      <span className="text-red-600">{err.errors.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleClear} data-testid="upload-another-btn">
                Subir otro archivo
              </Button>
              <Button className="btn-accent" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
                Ir al panel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
